import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Center,
  Checkbox,
  FormControl,
  Heading,
  HStack,
  Image,
  Input,
  Modal,
  ScrollView,
  Select,
  Spinner,
  Stack,
  Text,
  TextArea,
  Badge,
  View,
  VStack,
} from "native-base";
import { compose } from "redux";
import { connect, useDispatch } from "react-redux";
import { useFirestoreConnect } from "react-redux-firebase";
import { useSelector } from "react-redux";
import TimeInput from "react-time-picker-input";
import Notiflix from "notiflix";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import firebase from "firebase/app";
import "firebase/firestore";

import { AddMenu, MenuONOFF, MenuUpdate } from "../Redux/actions";
import { useHistory } from "react-router-dom";
import { parseGPTResponse, mergePageResults } from "../utils/cvMenuParser";

import { ENDPOINTS } from "../config";
import useStaffRole from "../hooks/useStaffRole";
import DoshiiMenuTooltip from "../components/integration/DoshiiMenuTooltip";
import DataSourceIndicator from "../components/integration/DataSourceIndicator";
import MenuSyncStatus from "../components/integration/MenuSyncStatus";

const CV_ENDPOINT = ENDPOINTS.cvUploadFile;

const MenuPage = (props) => {
  useEffect(() => { document.title = "Menu Management | Piatto"; }, []);
  const { auth } = props;
  const id = props.match.params.id;
  const { canManageMenus, canToggle, canViewOnly } = useStaffRole(id);

  // ── Existing "Add / Edit Menu" modal state ──────────────────────────────
  const [MenuModal, setMenuModal] = useState(false);
  const [MenuON, setMenuON] = useState("08:00");
  const [MenuOFF, setMenuOFF] = useState("21:30");
  const [MenuType, setMenuType] = useState("");
  const [MenuISOn, setMenuISOn] = useState(true);
  const [Dis, setDis] = useState("");
  const [menuName, setMenuName] = useState("");
  const [IsMenuUpdateRequest, setIsMenuUpdateRequest] = useState(false);
  const [MenuIDForUpdating, setMenuIDForUpdating] = useState("");

  // ── Import-from-Photo/PDF modal state ───────────────────────────────────
  const [importModalOpen, setImportModalOpen] = useState(false);
  // step: 0 = upload,  1 = processing,  2 = review
  const [importStep, setImportStep] = useState(0);
  const [importFile, setImportFile] = useState(null);
  const [importPreviewUrl, setImportPreviewUrl] = useState(null); // object URL for preview
  const [importError, setImportError] = useState(null);
  const [importSaving, setImportSaving] = useState(false);

  // Editable data derived from CV response
  const [importMenuName, setImportMenuName] = useState("");
  const [importMenuType, setImportMenuType] = useState("Food");
  const [importMenuDesc, setImportMenuDesc] = useState("");
  const [importDishes, setImportDishes] = useState([]); // [{ name, description, price }]

  const dispatch = useDispatch();
  const history = useHistory();

  useFirestoreConnect([
    {
      collection: "MenuDetails",
      where: [["menuBizId", "==", id]],
      orderBy: [["createdOn", "asc"]],
    },
    {
      collection: "Business",
      doc: id,
    },
  ]);
  const Menu = useSelector(({ firestore: { ordered } }) => ordered.MenuDetails);
  const businessData = useSelector(({ firestore: { data } }) => data.Business);
  const business = businessData ? businessData[id] : null;
  const isDoshii = business?.integration?.type === 'doshii' && business?.integration?.status === 'active';
  const integrationSource = isDoshii ? 'doshii' : 'none';

  // ── Existing handlers ────────────────────────────────────────────────────
  const handleEditMenu = (item) => {
    setIsMenuUpdateRequest(true);
    setDis(item.menuDesc);
    setMenuON(item.menuOnTime);
    setMenuOFF(item.menuOffTime);
    setMenuType(item.menuType);
    setMenuIDForUpdating(item.id);
    setMenuName(item.menuName);
    setMenuModal(true);
  };

  const handleSave = () => {
    if (MenuType.length > 1 && Dis.length > 0) {
      setMenuModal(false);
      const bizId = id;
      const data = {
        MenuType,
        MenuON,
        MenuOFF,
        MenuISOn,
        Dis,
        bizId,
        menuName,
      };
      dispatch(AddMenu(data));
    } else {
      Notiflix.Report.Warning(
        "Warning",
        `please fill all Menu information`,
        "Close"
      );
    }
  };

  // ── Import helpers ────────────────────────────────────────────────────────

  const resetImport = () => {
    setImportStep(0);
    if (importPreviewUrl) URL.revokeObjectURL(importPreviewUrl);
    setImportFile(null);
    setImportPreviewUrl(null);
    setImportError(null);
    setImportMenuName("");
    setImportMenuType("Food");
    setImportMenuDesc("");
    setImportDishes([]);
    setImportSaving(false);
  };

  const handleImportUpload = async () => {
    if (!importFile) {
      setImportError("Please select a menu photo or PDF first.");
      return;
    }

    setImportStep(1);
    setImportError(null);

    try {
      const formData = new FormData();
      formData.append("file", importFile);

      const response = await axios.post(CV_ENDPOINT, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000, // 60 s — GPT-4o can take a moment
      });

      const { jsonDataArray } = response.data;
      if (!jsonDataArray || jsonDataArray.length === 0) {
        throw new Error("No data returned from the menu analysis.");
      }

      const merged = mergePageResults(jsonDataArray);

      // Pre-populate review fields
      setImportMenuName(merged.menuName || "");
      setImportMenuDesc(merged.avail || "");
      setImportDishes(
        (merged.items || []).map((item) => ({
          id: uuidv4(),
          name: item.name || "",
          description: item.description || "",
          price: item.price ? String(item.price).replace(/[^0-9.]/g, "") : "",
          options: item.options || "",
        }))
      );

      setImportStep(2);
    } catch (err) {
      console.error("CV import error:", err);
      setImportError(
        err.response?.data || err.message || "Failed to analyse the menu. Please try again."
      );
      setImportStep(0);
    }
  };

  const updateDish = (index, field, value) => {
    setImportDishes((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeDish = (index) => {
    setImportDishes((prev) => prev.filter((_, i) => i !== index));
  };

  const addEmptyDish = () => {
    setImportDishes((prev) => [
      ...prev,
      { id: uuidv4(), name: "", description: "", price: "", options: "" },
    ]);
  };

  const handleSaveImport = async () => {
    if (!importMenuName.trim()) {
      setImportError("Please enter a menu name.");
      return;
    }
    if (!importMenuType) {
      setImportError("Please select a menu type.");
      return;
    }
    if (importDishes.length === 0) {
      setImportError("There are no dishes to import.");
      return;
    }

    setImportSaving(true);
    setImportError(null);

    try {
      const menuID = uuidv4();
      const db = firebase.firestore();

      // 1. Create the menu document
      await db
        .collection("MenuDetails")
        .doc(menuID)
        .set({
          menuBizId: id,
          menuName: importMenuName.trim(),
          menuDesc: importMenuDesc.trim(),
          menuOnTime: "08:00",
          menuOffTime: "21:30",
          menuOn: false,
          menuType: importMenuType,
          menuId: menuID,
          createdOn: new Date(),
          uid: auth.uid,
          importedFromPhoto: true,
          cvVerificationStatus: "pending",
        });

      // 2. Create each dish document in a batch
      const batch = db.batch();
      const dishesToSave = importDishes.filter((d) => d.name.trim());
      dishesToSave.forEach((dish) => {
        const dishID = uuidv4();
        const ref = db.collection("DishDetails").doc(dishID);
        batch.set(ref, {
          dishName: dish.name.trim(),
          dishDesc: dish.description.trim(),
          dishAvailSize: [
            {
              size: "Standard",
              price: parseFloat(dish.price) || 0,
            },
          ],
          dishOn: false,
          menuID: menuID,
          dishMenuId: menuID,
          bizId: id,
          dishID: dishID,
          DishImage: "",
          timestamp: new Date(),
          uid: auth.uid,
          importedFromPhoto: true,
        });
      });
      await batch.commit();

      Notiflix.Report.Success(
        "Menu Submitted for Review",
        `"${importMenuName}" with ${dishesToSave.length} dish${dishesToSave.length !== 1 ? "es" : ""} has been submitted. It will go live once verified by the Piatto team.`,
        "OK"
      );
      resetImport();
      setImportModalOpen(false);
    } catch (err) {
      console.error("Save import error:", err);
      setImportError("Failed to save the imported menu. Please try again.");
    } finally {
      setImportSaving(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <VStack role="main" px="4" my={{ base: "10", md: "1" }}>
      {/* Integration status indicators */}
      <DataSourceIndicator source={integrationSource} />
      {isDoshii && (
        <>
          <DoshiiMenuTooltip />
          <MenuSyncStatus
            lastSync={business?.integration?.lastMenuSync}
            syncStatus={business?.integration?.menuSyncStatus}
          />
        </>
      )}

      {/* Banner: menus toggled off */}
      {Menu && Menu.length > 0 && Menu.some((m) => !m.menuOn) && (
        <Box bg="amber.100" p={4} borderRadius="lg" borderWidth={1} borderColor="amber.400" mb={3}>
          <Text fontSize="sm" fontWeight="semibold" color="amber.800" mb={1}>
            {Menu.every((m) => !m.menuOn)
              ? `All ${Menu.length} menu${Menu.length !== 1 ? "s are" : " is"} toggled off`
              : `${Menu.filter((m) => !m.menuOn).length} of ${Menu.length} menu${Menu.length !== 1 ? "s" : ""} toggled off`}
            {" — customers can’t see them on Piatto yet"}
          </Text>
          <Text fontSize="xs" color="coolGray.600">
            Toggle each menu on using the On/Off button below to make it visible. You can also
            toggle individual dishes off if you sell out of a particular item. Toggled-off items
            cannot be seen or purchased on the app {"—"} preventing failed orders, confusion
            for your staff, and customers {"—"} including people with disabilities who may
            have made a significant effort to visit {"—"} from wasting time and money on
            something that is not available.
          </Text>
        </Box>
      )}

      <HStack flexWrap="wrap" alignItems="center" justifyContent="center">
        <Center w={"100%"}>
          <Button mb={1} onPress={() => history.goBack()} w={"100%"}>
            Back to Order Page
          </Button>
          {canManageMenus && !isDoshii && (
            <HStack w={"100%"} space={1}>
              <Button
                mb={2}
                colorScheme={"gray"}
                onPress={() => {
                  setIsMenuUpdateRequest(false);
                  setMenuON("08:00");
                  setMenuOFF("21:30");
                  setMenuType("");
                  setMenuISOn(true);
                  setDis("");
                  setMenuModal(true);
                }}
                flex={1}
              >
                Add New Menu
              </Button>

              {/* ── Import from Photo / PDF ─────────────────────────────── */}
              <Button
                mb={2}
                colorScheme={"violet"}
                onPress={() => {
                  resetImport();
                  setImportModalOpen(true);
                }}
                flex={1}
              >
                Import from Photo / PDF
              </Button>
            </HStack>
          )}
        </Center>

        {/* ── Menu cards ──────────────────────────────────────────────── */}
        {Menu &&
          Menu.map(function(item, i) {
            return (
              <Box key={i} m={2} textAlign={"center"} alignItems="center">
                <Box
                  maxW="80"
                  w={"80"}
                  minH={"150px"}
                  justifyContent="center"
                  rounded="lg"
                  overflow="hidden"
                  borderColor="coolGray.200"
                  borderWidth="1"
                  _web={{
                    shadow: 2,
                    borderWidth: 0,
                  }}
                  _light={{
                    backgroundColor: "gray.50",
                  }}
                >
                  <Stack space={3}>
                    <Badge
                      colorScheme={
                        item.menuType === "Food"
                          ? "red"
                          : item.menuType === "Drinks"
                          ? "blue"
                          : "grey"
                      }
                    >
                      {item.menuType}
                    </Badge>

                    <Stack
                      marginLeft={1}
                      marginRight={2}
                      marginBottom={2}
                      space={2}
                    >
                      {canManageMenus && !isDoshii && (
                        <Button
                          onPress={() => handleEditMenu(item)}
                          variant={"outline"}
                          size={"xs"}
                          position={"absolute"}
                          right={0}
                        >
                          Edit
                        </Button>
                      )}
                      {canManageMenus && isDoshii && (
                        <Button
                          onPress={() => alert("Please make menu edits via your POS Menu Manager. Changes will sync to Piatto automatically.")}
                          variant={"outline"}
                          size={"xs"}
                          position={"absolute"}
                          right={0}
                          colorScheme={"coolGray"}
                        >
                          Synced
                        </Button>
                      )}

                      <Heading size="md" ml="-1">
                        {item.menuName}
                      </Heading>
                      <Text>{item.menuDesc}</Text>
                      <HStack space={10} justifyContent={"center"}>
                        <Text fontWeight={"bold"}>From {item.menuOnTime}</Text>
                        <Text fontWeight={"bold"}>To {item.menuOffTime}</Text>
                      </HStack>
                      <Text
                        color={item.menuOn ? "green.500" : "red.500"}
                        fontWeight={"bold"}
                      >
                        status {item.menuOn ? "ON" : "OFF"}
                      </Text>

                      {canManageMenus && !isDoshii && (
                        <Button
                          onPress={() =>
                            dispatch(MenuONOFF(item.id, item.menuOn))
                          }
                          colorScheme={"warmGray"}
                          w={"100%"}
                        >
                          On/Off
                        </Button>
                      )}

                      <Button
                        onPress={() =>
                          isDoshii
                            ? alert("Please make menu edits via your POS Menu Manager. Changes will sync to Piatto automatically.")
                            : history.push(
                                `/DishDetails/${item.menuBizId}/${item.menuId}`
                              )
                        }
                        colorScheme={isDoshii ? "coolGray" : "warmGray"}
                      >
                        {isDoshii ? "View Dishes (Read Only)" : "Manage dishes"}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Box>
            );
          })}
      </HStack>

      {/* ── Add / Edit Menu modal ─────────────────────────────────────────── */}
      <Modal
        isOpen={MenuModal}
        onClose={() => {
          setIsMenuUpdateRequest(false);
          setMenuON("08:00");
          setMenuOFF("21:30");
          setMenuType("");
          setMenuISOn(true);
          setDis("");
          setMenuModal(false);
          setMenuName("");
        }}
        size={"lg"}
        aria-label={IsMenuUpdateRequest ? "Edit Menu" : "Add New Menu"}
      >
        <Modal.Content>
          <Modal.CloseButton />
          <Modal.Header>
            {IsMenuUpdateRequest ? "Edit Menu" : "Add New Menu"}
          </Modal.Header>
          <Modal.Body>
            <VStack space={1}>
              <FormControl.Label>Name:</FormControl.Label>
              <Input
                placeholder="Name"
                value={menuName}
                onChangeText={(e) => {
                  setMenuName(e);
                }}
              />

              <FormControl.Label>Description:</FormControl.Label>
              <TextArea
                onChangeText={(e) => setDis(e)}
                value={Dis}
                h={20}
                placeholder="Description"
              />
              <HStack
                justifyContent={"center"}
                zIndex={9}
                space={10}
                w={"100%"}
              >
                <VStack>
                  <FormControl.Label>Menu on time:</FormControl.Label>
                  <TimeInput
                    value={MenuON}
                    fullTimeDropdown
                    onChange={(dateString) => setMenuON(dateString)}
                  />
                </VStack>
                <VStack>
                  <FormControl.Label>Menu off time:</FormControl.Label>
                  <TimeInput
                    value={MenuOFF}
                    fullTimeDropdown
                    onChange={(dateString) => setMenuOFF(dateString)}
                  />
                </VStack>
              </HStack>
              <HStack alignContent={"center"} space={2} w={"100%"}>
                <VStack w={IsMenuUpdateRequest ? "100%" : "80%"}>
                  <FormControl.Label>Menu Type:</FormControl.Label>
                  <Select
                    selectedValue={MenuType}
                    onValueChange={(itemValue) => setMenuType(itemValue)}
                    accessibilityLabel="Select your Your Menu type"
                    placeholder="Select your Your Menu type"
                  >
                    <Select.Item label="Food" value="Food" />
                    <Select.Item label="Drinks" value="Drinks" />
                    <Select.Item label="Goods" value="Goods" />
                  </Select>
                </VStack>
                {!IsMenuUpdateRequest && (
                  <VStack w={"20%"}>
                    <FormControl.Label mb={2}>
                      <Text fontWeight={"bold"} color="red.500">
                        Menu On:
                      </Text>
                    </FormControl.Label>
                    <Checkbox
                      onChange={(e) => setMenuISOn(e)}
                      defaultIsChecked={MenuISOn}
                      value={MenuISOn}
                    />
                  </VStack>
                )}
              </HStack>
            </VStack>
          </Modal.Body>
          <Modal.Footer>
            <HStack space={2}>
              <Button
                variant="ghost"
                colorScheme="blueGray"
                onPress={() => {
                  setIsMenuUpdateRequest(false);
                  setMenuON("08:00");
                  setMenuOFF("21:30");
                  setMenuType("");
                  setMenuISOn(true);
                  setDis("");
                  setMenuModal(false);
                }}
              >
                Cancel
              </Button>
              {!IsMenuUpdateRequest && (
                <Button
                  onPress={() => {
                    handleSave();
                  }}
                >
                  Save
                </Button>
              )}
              {IsMenuUpdateRequest && (
                <Button
                  onPress={() => {
                    if (
                      Dis.length > 0 &&
                      MenuON.length > 0 &&
                      MenuOFF.length > 0 &&
                      MenuType.length > 0 &&
                      MenuIDForUpdating.length > 0
                    ) {
                      const data = {
                        Dis,
                        MenuON,
                        MenuOFF,
                        MenuType,
                        MenuIDForUpdating,
                        menuName,
                      };

                      dispatch(MenuUpdate(data));
                      setMenuModal(false);
                    } else {
                      Notiflix.Report.Warning(
                        "Warning",
                        `please fill all Menu information`,
                        "Close"
                      );
                    }
                  }}
                >
                  Update
                </Button>
              )}
            </HStack>
          </Modal.Footer>
        </Modal.Content>
      </Modal>

      {/* ── Import from Photo / PDF modal ────────────────────────────────── */}
      <Modal
        isOpen={importModalOpen}
        onClose={() => {
          if (importStep !== 1) {
            resetImport();
            setImportModalOpen(false);
          }
        }}
        size={"xl"}
        aria-label="Import Menu from Photo or PDF"
      >
        <Modal.Content maxH="90%">
          <Modal.CloseButton isDisabled={importStep === 1} />
          <Modal.Header>
            {importStep === 0 && "Import Menu from Photo / PDF"}
            {importStep === 1 && "Analysing Your Menu..."}
            {importStep === 2 && `Review Extracted Menu (${importDishes.length} dishes)`}
          </Modal.Header>

          <Modal.Body>
            {/* ── Step 0: Upload ────────────────────────────────────── */}
            {importStep === 0 && (
              <VStack space={4}>
                <Text fontSize="sm" color="coolGray.600">
                  Upload a photo or PDF of your menu. GPT-4o will extract dish
                  names, descriptions and prices. You can review and edit
                  everything before saving.
                </Text>

                {importError && (
                  <Box
                    bg="red.100"
                    borderRadius="md"
                    px={3}
                    py={2}
                  >
                    <Text color="red.700" fontSize="sm">{importError}</Text>
                  </Box>
                )}

                {/* Native file input styled as a button */}
                <Box>
                  <FormControl.Label>Select file (JPEG, PNG, WEBP, GIF or PDF):</FormControl.Label>
                  <Button
                    variant="outline"
                    colorScheme="violet"
                    onPress={() =>
                      document.getElementById("cv-file-input").click()
                    }
                  >
                    {importFile ? importFile.name : "Choose File"}
                  </Button>
                  <input
                    id="cv-file-input"
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                    aria-label="Upload menu photo or PDF"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files[0] || null;
                      // Clean up previous preview URL
                      if (importPreviewUrl) URL.revokeObjectURL(importPreviewUrl);
                      if (file) {
                        const url = URL.createObjectURL(file);
                        setImportPreviewUrl(url);
                      } else {
                        setImportPreviewUrl(null);
                      }
                      setImportFile(file);
                      setImportError(null);
                    }}
                  />
                </Box>

                {/* ── File preview ──────────────────────────────────── */}
                {importFile && (
                  <Box
                    borderWidth={1}
                    borderColor="violet.200"
                    borderRadius="md"
                    p={3}
                    bg="violet.50"
                    alignItems="center"
                  >
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      color="violet.700"
                      mb={2}
                      alignSelf="flex-start"
                    >
                      PREVIEW
                    </Text>

                    {importFile.type === "application/pdf" ? (
                      /* PDF: embed a scrollable in-browser preview */
                      <Box w="100%" h="400px" borderRadius="md" overflow="hidden">
                        <embed
                          src={importPreviewUrl}
                          type="application/pdf"
                          width="100%"
                          height="100%"
                          title="Menu PDF preview"
                          aria-label="PDF preview"
                        />
                      </Box>
                    ) : (
                      /* Image: show full-width thumbnail */
                      <Box
                        w="100%"
                        maxH="400px"
                        overflow="hidden"
                        borderRadius="md"
                        alignItems="center"
                      >
                        <img
                          src={importPreviewUrl}
                          alt="Menu preview"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "400px",
                            objectFit: "contain",
                            borderRadius: 6,
                          }}
                        />
                      </Box>
                    )}

                    <Text fontSize="xs" color="coolGray.600" mt={2} alignSelf="flex-start">
                      {importFile.name} — {(importFile.size / 1024).toFixed(1)} KB
                    </Text>

                    <Text fontSize="xs" color="violet.600" mt={1} textAlign="center">
                      Check the preview above looks correct, then tap "Analyse Menu".
                    </Text>
                  </Box>
                )}
              </VStack>
            )}

            {/* ── Step 1: Processing ────────────────────────────────── */}
            {importStep === 1 && (
              <VStack space={4} alignItems="center" py={8} role="status" aria-live="polite">
                <Spinner size="lg" color="violet.500" accessibilityLabel="Loading" />
                <Text fontWeight="bold" fontSize="md">
                  Analysing your menu...
                </Text>
                <Text fontSize="sm" color="coolGray.600" textAlign="center">
                  GPT-4o is reading your menu. This usually takes 10–30 seconds.
                  Please don't close this window.
                </Text>
              </VStack>
            )}

            {/* ── Step 2: Review / Edit ─────────────────────────────── */}
            {importStep === 2 && (
              <ScrollView>
                <VStack space={4}>
                  {importError && (
                    <Box bg="red.100" borderRadius="md" px={3} py={2}>
                      <Text color="red.700" fontSize="sm">{importError}</Text>
                    </Box>
                  )}

                  {/* Menu-level fields */}
                  <Box
                    bg="violet.50"
                    borderRadius="md"
                    p={3}
                    borderWidth={1}
                    borderColor="violet.200"
                  >
                    <Text
                      fontWeight="bold"
                      fontSize="sm"
                      color="violet.700"
                      mb={2}
                    >
                      MENU DETAILS
                    </Text>

                    <FormControl.Label>Menu Name:</FormControl.Label>
                    <Input
                      value={importMenuName}
                      onChangeText={setImportMenuName}
                      placeholder="Menu name"
                      mb={2}
                    />

                    <FormControl.Label>Menu Type:</FormControl.Label>
                    <Select
                      selectedValue={importMenuType}
                      onValueChange={setImportMenuType}
                      accessibilityLabel="Select menu type"
                      placeholder="Select menu type"
                      mb={2}
                    >
                      <Select.Item label="Food" value="Food" />
                      <Select.Item label="Drinks" value="Drinks" />
                      <Select.Item label="Goods" value="Goods" />
                    </Select>

                    <FormControl.Label>
                      Availability / Description (e.g. "Mon–Fri, 8am–11am"):
                    </FormControl.Label>
                    <Input
                      value={importMenuDesc}
                      onChangeText={setImportMenuDesc}
                      placeholder="Availability or description"
                    />
                  </Box>

                  {/* Dish rows */}
                  <HStack justifyContent="space-between" alignItems="center">
                    <Text fontWeight="bold" fontSize="sm" color="coolGray.700">
                      DISHES ({importDishes.length})
                    </Text>
                    <Button
                      size="xs"
                      colorScheme="green"
                      onPress={addEmptyDish}
                    >
                      + Add Row
                    </Button>
                  </HStack>

                  {importDishes.length === 0 && (
                    <Text
                      fontSize="sm"
                      color="coolGray.600"
                      textAlign="center"
                      py={4}
                    >
                      No dishes extracted. Tap "+ Add Row" to add dishes manually.
                    </Text>
                  )}

                  {importDishes.map((dish, index) => (
                    <Box
                      key={dish.id}
                      borderWidth={1}
                      borderColor="coolGray.200"
                      borderRadius="md"
                      p={3}
                      bg="white"
                    >
                      <HStack
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                      >
                        <Text
                          fontWeight="semibold"
                          fontSize="xs"
                          color="coolGray.600"
                        >
                          DISH {index + 1}
                        </Text>
                        <Button
                          size="xs"
                          colorScheme="danger"
                          variant="ghost"
                          onPress={() => removeDish(index)}
                          accessibilityLabel={`Remove dish ${index + 1}`}
                        >
                          Remove
                        </Button>
                      </HStack>

                      <FormControl.Label>Name:</FormControl.Label>
                      <Input
                        value={dish.name}
                        onChangeText={(v) => updateDish(index, "name", v)}
                        placeholder="Dish name"
                        mb={2}
                      />

                      <FormControl.Label>Description:</FormControl.Label>
                      <TextArea
                        value={dish.description}
                        onChangeText={(v) => updateDish(index, "description", v)}
                        placeholder="Description"
                        h={16}
                        mb={2}
                      />

                      <HStack space={2}>
                        <VStack flex={1}>
                          <FormControl.Label>Price ($):</FormControl.Label>
                          <Input
                            value={dish.price}
                            onChangeText={(v) =>
                              updateDish(
                                index,
                                "price",
                                v.replace(/[^0-9.]/g, "")
                              )
                            }
                            placeholder="0.00"
                            keyboardType="numeric"
                          />
                        </VStack>
                        <VStack flex={2}>
                          <FormControl.Label>
                            Dietary tags (e.g. GF, V):
                          </FormControl.Label>
                          <Input
                            value={dish.options}
                            onChangeText={(v) =>
                              updateDish(index, "options", v)
                            }
                            placeholder="GF, GFO, V, VEG..."
                          />
                        </VStack>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              </ScrollView>
            )}
          </Modal.Body>

          <Modal.Footer>
            <HStack space={2} justifyContent="flex-end" w="100%">
              {/* Cancel / Back */}
              {importStep !== 1 && (
                <Button
                  variant="ghost"
                  colorScheme="blueGray"
                  onPress={() => {
                    if (importStep === 2) {
                      setImportStep(0);
                    } else {
                      resetImport();
                      setImportModalOpen(false);
                    }
                  }}
                >
                  {importStep === 2 ? "Back" : "Cancel"}
                </Button>
              )}

              {/* Upload → analyse */}
              {importStep === 0 && (
                <Button
                  colorScheme="violet"
                  onPress={handleImportUpload}
                  isDisabled={!importFile}
                >
                  Analyse Menu
                </Button>
              )}

              {/* Confirm import */}
              {importStep === 2 && (
                <Button
                  colorScheme="green"
                  onPress={handleSaveImport}
                  isLoading={importSaving}
                  isLoadingText="Saving..."
                  isDisabled={importSaving}
                >
                  Import {importDishes.filter((d) => d.name.trim()).length} Dishes
                </Button>
              )}
            </HStack>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    </VStack>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.firebase.auth,
  };
};

export default compose(connect(mapStateToProps))(MenuPage);
